import { useEffect, useRef, useState } from "react";
import { getApiRequestHeaders } from "@workspace/api-client-react";

export type HubQuality = "LIVE" | "DELAYED" | "STALE" | "UNAVAILABLE" | string;
export type HubUnit = "troy_ounce" | "pound";
export type HubQuote = {
  metal: string;
  currency: string;
  value: number | null;
  canonicalUnit: HubUnit;
  source?: string;
  sourceTimestamp?: string | null;
  retrievedAt: string;
  quality: HubQuality;
  sequence: number;
  provenance?: Record<string, unknown>;
  change?: number | null;
  changePct?: number | null;
};
export type HubSnapshot = { sequence: number; retrievedAt: string; quality: HubQuality; quotes: HubQuote[] };

export function projectHubQuoteUnit(
  quote: HubQuote,
  requestedUnit: string,
): { value: number | null; change: number | null; unit: "troy_ounce" | "pound" } {
  const grams = { troy_ounce: 31.1034768, pound: 453.59237 } as const;
  const targetUnit = requestedUnit === "pound" ? "pound" : "troy_ounce";
  const factor = grams[targetUnit] / grams[quote.canonicalUnit];
  return {
    value: quote.value == null ? null : quote.value * factor,
    change: quote.change == null ? null : quote.change * factor,
    unit: targetUnit,
  };
}
export type LiveMarketEvent =
  | { kind: "snapshot" | "price"; payload: HubSnapshot }
  | { kind: "status"; payload: {
      sequence?: number; quality: HubQuality; retrievedAt?: string;
      quotes?: Array<Pick<HubQuote, "metal" | "currency" | "quality">>;
    } }
  | { kind: "heartbeat"; payload: { sequence?: number; at?: string } };

export function applyPerQuoteQualityStatus<T extends { metal: string; currency?: string; quality: string }>(
  current: T[],
  status: Extract<LiveMarketEvent, { kind: "status" }>["payload"],
): T[] {
  if (!status.quotes?.length) return current;
  const qualities = new Map(status.quotes.map((quote) => [
    `${quote.metal}:${quote.currency ?? ""}`,
    quote.quality,
  ]));
  return current.map((quote) => {
    const quality = qualities.get(`${quote.metal}:${quote.currency ?? ""}`)
      ?? qualities.get(`${quote.metal}:`);
    return quality ? { ...quote, quality } : quote;
  });
}

export type LiveMarketState<T> = {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  disconnected: boolean;
  ageMs: number | null;
  quality: HubQuality;
  sequence: number;
};

function ageFrom(timestamp: string | undefined): number | null {
  if (!timestamp) return null;
  const value = Date.parse(timestamp);
  return Number.isFinite(value) ? Math.max(0, Date.now() - value) : null;
}

type Options<T> = {
  snapshotUrl: string;
  streamUrl: string;
  /**
   * Versioned compatibility boundary for cross-tab state sharing. Consumers
   * may share this key only when their adapted data shapes are compatible.
   * Without a key, this hook keeps its connection local to the current tab.
   */
  coordinationKey?: string;
  parseSnapshot?: (value: unknown) => { data: T; snapshot?: HubSnapshot };
  fallbackUrl?: string;
  parseFallback?: (value: unknown) => { data: T; snapshot?: HubSnapshot };
  applyEvent?: (current: T, event: LiveMarketEvent) => T;
  initialData?: T;
  authenticated?: boolean;
};

const asSnapshot = (value: unknown): HubSnapshot | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const v = value as Partial<HubSnapshot>;
  return Array.isArray(v.quotes) && typeof v.sequence === "number" ? v as HubSnapshot : undefined;
};

const RETRY_BASE_MS = 1_000;
const RETRY_MAX_MS = 30_000;
const OWNER_LEASE_MS = 6_000;
const OWNER_TICK_MS = 2_000;
let nextMarketTabId = 0;

type SharedMarketMessage<T> =
  | { type: "state"; ownerId: string; state: LiveMarketState<T> }
  | { type: "release"; ownerId: string }
  | { type: "request"; requesterId: string };

type MarketLease = { ownerId: string; expiresAt: number };

const readLease = (key: string): MarketLease | undefined => {
  try {
    const lease = JSON.parse(localStorage.getItem(key) ?? "null") as Partial<MarketLease> | null;
    return lease && typeof lease.ownerId === "string" && typeof lease.expiresAt === "number"
      ? lease as MarketLease
      : undefined;
  } catch {
    return undefined;
  }
};

const writeLease = (key: string, lease: MarketLease): boolean => {
  try {
    localStorage.setItem(key, JSON.stringify(lease));
    return readLease(key)?.ownerId === lease.ownerId;
  } catch {
    return false;
  }
};

const coordinationKey = (value: string): string => {
  let first = 0xdeadbeef;
  let second = 0x41c6ce57;
  for (let index = 0; index < value.length; index++) {
    const code = value.charCodeAt(index);
    first = Math.imul(first ^ code, 2654435761);
    second = Math.imul(second ^ code, 1597334677);
  }
  first = Math.imul(first ^ (first >>> 16), 2246822507) ^ Math.imul(second ^ (second >>> 13), 3266489909);
  second = Math.imul(second ^ (second >>> 16), 2246822507) ^ Math.imul(first ^ (first >>> 13), 3266489909);
  return `${(first >>> 0).toString(36)}${(second >>> 0).toString(36)}`;
};

export function marketRetryDelayMs(attempt: number, retryAfterSec = 0, random = Math.random()): number {
  const exponential = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * (2 ** Math.max(0, attempt)));
  const jittered = Math.round(exponential * (0.75 + Math.max(0, Math.min(1, random)) * 0.5));
  return Math.min(RETRY_MAX_MS, Math.max(jittered, Math.ceil(retryAfterSec * 1_000)));
}

async function readSseStream(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
  onEvent: (event: string, data: string) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let eventName = "message";
  let dataLines: string[] = [];

  const dispatch = () => {
    if (dataLines.length) onEvent(eventName, dataLines.join("\n"));
    eventName = "message";
    dataLines = [];
  };

  const processLine = (line: string) => {
    if (line === "") {
      dispatch();
      return;
    }
    if (line.startsWith(":")) return;

    const separator = line.indexOf(":");
    const field = separator < 0 ? line : line.slice(0, separator);
    const value = separator < 0
      ? ""
      : line.slice(separator + 1).replace(/^ /, "");
    if (field === "event") eventName = value;
    if (field === "data") dataLines.push(value);
  };

  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      if (done) break;

      let newline = buffer.indexOf("\n");
      while (newline >= 0) {
        const line = buffer.slice(0, newline).replace(/\r$/, "");
        buffer = buffer.slice(newline + 1);
        processLine(line);
        newline = buffer.indexOf("\n");
      }
    }

    if (buffer) processLine(buffer.replace(/\r$/, ""));
    if (!signal.aborted) dispatch();
  } finally {
    reader.releaseLock();
  }
}

async function retryAfterSeconds(response: Response): Promise<number> {
  const header = Number(response.headers.get("Retry-After"));
  if (Number.isFinite(header) && header > 0) return header;
  try {
    const body = await response.clone().json() as { retryAfterSec?: unknown };
    const value = Number(body.retryAfterSec);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

export function useLiveMarket<T>(options: Options<T>): LiveMarketState<T> {
  const [state, setState] = useState<LiveMarketState<T>>({
    data: options.initialData, isLoading: !options.initialData, isError: false,
    disconnected: false, ageMs: null, quality: "UNAVAILABLE", sequence: -1,
  });
  const stateRef = useRef(state);
  stateRef.current = state;
  const parser = options.parseSnapshot ?? ((value: unknown) => ({ data: value as T, snapshot: asSnapshot(value) }));

  useEffect(() => {
    let disposed = false;
    let source: EventSource | undefined;
    let reconnect: number | undefined;
    let ageTimer: number | undefined;
    let ownerTimer: number | undefined;
    let retryAttempt = 0;
    let fallbackAttempted = false;
    let ownsConnection = false;
    let connectionEpoch = 0;
    let pendingRequest: AbortController | undefined;
    const tabId = `${Date.now()}-${nextMarketTabId++}-${Math.random().toString(36).slice(2)}`;
    const channelName = options.coordinationKey
      ? `live-market:${coordinationKey(`${options.snapshotUrl}\0${options.streamUrl}\0${options.coordinationKey}`)}`
      : undefined;
    const leaseKey = channelName ? `__${channelName}:owner` : "";
    let coordinationActive = false;
    let channel: BroadcastChannel | undefined;
    try {
      if (channelName && typeof BroadcastChannel !== "undefined" && typeof localStorage !== "undefined") {
        channel = new BroadcastChannel(channelName);
        coordinationActive = true;
      }
    } catch {
      channel = undefined;
    }

    const publishState = (next: LiveMarketState<T>) => {
      if (ownsConnection) channel?.postMessage({ type: "state", ownerId: tabId, state: next } satisfies SharedMarketMessage<T>);
      return next;
    };

    const updateState = (updater: (current: LiveMarketState<T>) => LiveMarketState<T>) => {
      setState(current => publishState(updater(current)));
    };

    const marketFetch = async (url: string, init: RequestInit = {}) => {
      const headers = new Headers(init.headers);
      const requestHeaders = options.authenticated
        ? await getApiRequestHeaders(headers)
        : headers;
      return fetch(url, {
        credentials: "include",
        ...init,
        headers: requestHeaders,
      });
    };

    const accept = (event: LiveMarketEvent) => {
      if (disposed) return;
      const current = stateRef.current;
      const incomingSequence = event.payload.sequence;
      if (incomingSequence != null && incomingSequence < current.sequence) return;
      if (event.kind === "heartbeat") {
        updateState(s => ({ ...s, disconnected: false }));
        return;
      }
      if (event.kind === "status") {
        updateState(s => ({
          ...s,
          data: options.applyEvent && s.data ? options.applyEvent(s.data, event) : s.data,
          quality: event.payload.quality,
          disconnected: false,
        }));
        return;
      }
      // Consumers with an event adapter often hydrate a richer REST envelope
      // (branding/config plus quotes). Never parse an SSE HubSnapshot as that
      // unrelated envelope when REST hydration was unavailable.
      const parsed = event.kind === "snapshot" && !options.applyEvent ? parser(event.payload) : undefined;
      if (event.kind === "snapshot") retryAttempt = 0;
      updateState(s => {
        const data = options.applyEvent
          ? s.data
            ? options.applyEvent(s.data, event)
            : parser(event.payload).data
          : parsed?.data ?? s.data;
        const snap = parsed?.snapshot ?? event.payload;
        return { ...s, data, isLoading: false, isError: false, disconnected: false,
          quality: snap.quality, sequence: snap.sequence, ageMs: ageFrom(snap.retrievedAt) };
      });
    };

    const scheduleReconnect = (retryAfterSec = 0) => {
      if (disposed || reconnect) return;
      const delay = marketRetryDelayMs(retryAttempt++, retryAfterSec);
      reconnect = window.setTimeout(() => {
        reconnect = undefined;
        void connect();
      }, delay);
    };

    const hydrateFallback = async (signal?: AbortSignal, isCurrent = () => true) => {
      if (fallbackAttempted || !options.fallbackUrl || !options.parseFallback) return;
      fallbackAttempted = true;
      const response = await marketFetch(options.fallbackUrl, { signal });
      if (!response.ok) throw new Error(`Market fallback failed (${response.status})`);
      const parsed = options.parseFallback(await response.json());
      if (disposed || !isCurrent()) return;
      const snap = parsed.snapshot;
       updateState(s => ({
        ...s,
        data: parsed.data,
        isLoading: false,
        isError: false,
        quality: snap?.quality ?? s.quality,
        sequence: snap?.sequence ?? s.sequence,
        ageMs: snap ? ageFrom(snap.retrievedAt) : s.ageMs,
      }));
    };

    const connect = async () => {
      if (disposed || (coordinationActive && !ownsConnection)) return;
      const epoch = ++connectionEpoch;
      pendingRequest?.abort();
      const request = new AbortController();
      pendingRequest = request;
      const isCurrentOwner = () => {
        if (disposed || epoch !== connectionEpoch || request.signal.aborted) return false;
        return !coordinationActive || (ownsConnection && readLease(leaseKey)?.ownerId === tabId);
      };
      if (reconnect) {
        window.clearTimeout(reconnect);
        reconnect = undefined;
      }
      source?.close();
      updateState(s => ({ ...s, disconnected: true }));
      try {
        const response = await marketFetch(options.snapshotUrl, { signal: request.signal });
        if (!isCurrentOwner()) return;
        if (!response.ok) {
          await hydrateFallback(request.signal, isCurrentOwner).catch(() => undefined);
          if (!isCurrentOwner()) return;
          updateState(s => ({ ...s, isLoading: false, isError: !s.data }));
          scheduleReconnect(await retryAfterSeconds(response));
          return;
        }
        const parsed = parser(await response.json());
        if (!isCurrentOwner()) return;
        const snap = parsed.snapshot;
        updateState(s => ({
          ...s,
          data: parsed.data,
          isLoading: false,
          isError: false,
          quality: snap?.quality ?? s.quality,
          sequence: snap?.sequence ?? s.sequence,
          ageMs: snap ? ageFrom(snap.retrievedAt) : s.ageMs,
        }));
      } catch {
        if (!isCurrentOwner()) return;
        await hydrateFallback(request.signal, isCurrentOwner).catch(() => undefined);
        if (!isCurrentOwner()) return;
        updateState(s => ({ ...s, isLoading: false, isError: !s.data }));
        scheduleReconnect();
        return;
      }
      if (!isCurrentOwner()) return;
      if (options.authenticated) {
        void (async () => {
          try {
            const streamResponse = await marketFetch(options.streamUrl, {
              headers: { Accept: "text/event-stream" },
              signal: request.signal,
            });
            if (!isCurrentOwner()) return;
            if (!streamResponse.ok || !streamResponse.body) {
              const retryAfter = await retryAfterSeconds(streamResponse);
              if (streamResponse.body) {
                await streamResponse.body.cancel().catch(() => undefined);
              }
              if (!isCurrentOwner()) return;
              updateState(s => ({ ...s, disconnected: true }));
              scheduleReconnect(retryAfter);
              return;
            }

            updateState(s => ({ ...s, disconnected: false }));
            await readSseStream(streamResponse.body, request.signal, (eventName, data) => {
              if (
                eventName !== "snapshot" &&
                eventName !== "price" &&
                eventName !== "status" &&
                eventName !== "heartbeat"
              ) return;
              try {
                accept({ kind: eventName, payload: JSON.parse(data) });
              } catch {
                // Ignore malformed events and keep the live connection open.
              }
            });
            if (!isCurrentOwner()) return;
            updateState(s => ({ ...s, disconnected: true }));
            scheduleReconnect();
          } catch {
            if (!isCurrentOwner()) return;
            updateState(s => ({ ...s, disconnected: true }));
            scheduleReconnect();
          }
        })();
      } else {
        source = new EventSource(options.streamUrl);
        const handle = (kind: LiveMarketEvent["kind"]) => (message: MessageEvent) => {
          try { accept({ kind, payload: JSON.parse(message.data) }); } catch { /* malformed events are ignored */ }
        };
        source.addEventListener("snapshot", handle("snapshot"));
        source.addEventListener("price", handle("price"));
        source.addEventListener("status", handle("status"));
        source.addEventListener("heartbeat", handle("heartbeat"));
        source.onopen = () => updateState(s => ({ ...s, disconnected: false }));
        source.onerror = () => {
          source?.close();
          source = undefined;
          updateState(s => ({ ...s, disconnected: true }));
          scheduleReconnect();
        };
      }
    };

    const stopOwning = () => {
      ownsConnection = false;
      connectionEpoch++;
      pendingRequest?.abort();
      pendingRequest = undefined;
      source?.close();
      source = undefined;
      if (reconnect) window.clearTimeout(reconnect);
      reconnect = undefined;
    };

    const tryClaimOwnership = () => {
      if (disposed || !coordinationActive) return;
      const now = Date.now();
      const lease = readLease(leaseKey);
      if (lease && lease.ownerId !== tabId && lease.expiresAt > now) {
        if (ownsConnection) stopOwning();
        return;
      }
      const claimed = writeLease(leaseKey, { ownerId: tabId, expiresAt: now + OWNER_LEASE_MS });
      if (!claimed) {
        coordinationActive = false;
        channel?.close();
        channel = undefined;
        if (ownerTimer) window.clearInterval(ownerTimer);
        ownerTimer = undefined;
        if (!ownsConnection) {
          ownsConnection = true;
          void connect();
        }
        return;
      }
      if (!ownsConnection) {
        ownsConnection = true;
        void connect();
      } else {
        writeLease(leaseKey, { ownerId: tabId, expiresAt: now + OWNER_LEASE_MS });
      }
    };

    if (channel) {
      channel.onmessage = (message: MessageEvent<SharedMarketMessage<T>>) => {
        if (disposed) return;
        if (message.data.type === "request") {
          if (ownsConnection && readLease(leaseKey)?.ownerId === tabId) {
            channel?.postMessage({ type: "state", ownerId: tabId, state: stateRef.current } satisfies SharedMarketMessage<T>);
          }
          return;
        }
        if (message.data.ownerId === tabId) return;
        if (message.data.type === "state") {
          const lease = readLease(leaseKey);
          if (lease?.ownerId !== message.data.ownerId) return;
          stopOwning();
          setState(message.data.state);
        } else {
          window.setTimeout(tryClaimOwnership, Math.round(Math.random() * 100));
        }
      };
    }

    if (!options.snapshotUrl || !options.streamUrl) {
      setState(s => ({ ...s, isLoading: false, isError: true }));
      return () => { disposed = true; };
    }
    if (coordinationActive) {
      tryClaimOwnership();
      if (coordinationActive && !ownsConnection) {
        channel?.postMessage({ type: "request", requesterId: tabId } satisfies SharedMarketMessage<T>);
      }
      if (coordinationActive) ownerTimer = window.setInterval(tryClaimOwnership, OWNER_TICK_MS);
    } else {
      ownsConnection = true;
      void connect();
    }
    ageTimer = window.setInterval(() => setState(s => ({ ...s, ageMs: s.ageMs == null ? null : s.ageMs + 1000 })), 1000);
    return () => {
      disposed = true;
      pendingRequest?.abort();
      source?.close();
      if (reconnect) clearTimeout(reconnect);
      if (ageTimer) clearInterval(ageTimer);
      if (ownerTimer) clearInterval(ownerTimer);
      if (ownsConnection && coordinationActive && readLease(leaseKey)?.ownerId === tabId) {
        try { localStorage.removeItem(leaseKey); } catch { /* storage may be blocked */ }
        channel?.postMessage({ type: "release", ownerId: tabId } satisfies SharedMarketMessage<T>);
      }
      channel?.close();
    };
  }, [options.snapshotUrl, options.streamUrl, options.fallbackUrl, options.authenticated, options.coordinationKey]);
  return state;
}

export function hubSnapshot(value: unknown): { data: HubSnapshot; snapshot: HubSnapshot } {
  const snapshot = asSnapshot(value);
  if (!snapshot) throw new Error("Invalid market snapshot");
  return { data: snapshot, snapshot };
}